/* eslint-disable @typescript-eslint/no-explicit-any */
import useSWRInfinite from 'swr/infinite';
import projectsClient from '../utils/apiClients/projectClient';
import { dataURLToFile } from '../utils/fileUtils';
import api from '../utils/api';
import useSWR from 'swr';
import { ConceptAsset } from '../components/creator/Dashboard/Projects/ProjectCards';
import { ProjectStatusEnum } from '../types/ProjectStatus';
import { Influencer, LicenseType } from '../components/context/InfluencerContext';
import { Creator, SocialNetwork, Website } from '../components/context/CreatorContext';
import { ImageStatusEnum } from '../types/ImageGenerationStatus';
const PAGE_SIZE = 10;
const fetcher = (url: string) => projectsClient.get(url).then(res => res.data);

export interface ProjectRequest {
  influencerComment: string;
  status: ProjectStatusEnum;
  approvalDate: string;
  createdAt: string;
  updatedAt: string;
}
export interface Project {
  licensePrice?: number;
  licenseType?: string;
  licenseDuration?: number | string;
  licenseBegins?: string;
  id: string;
  creatorId: string;
  influencerId: string;
  name: string;
  description: string;
  notes: string;
  projectRequest: ProjectRequest;
  createdAt: string;
  updatedAt: string;
  onUpdate?: () => void;
  artStyleId?: string | null;
  statusHistory?: Array<any>;
  /* ------------------- */

  // creator?: Creator;
  influencer?: Influencer;
  rejectedAt?: string;
  approvedAt?: string;
  additionalCount?: number;
  reason?: string | null;
  conceptAssets?: Array<ConceptAsset>;
  title?: string;
  /* ----------------------------------- */
  license?: {
    licensePrice?: number;
    type?: LicenseType;
    duration?: number;
    beginAt?: string;
  };
  creator?: {
    id?: string;
    about?: string;
    categories?: string[];
    // socialNetworks?: string[],
    username?: string;
    profilePicUrl?: string;
    fullName?: string;

    tagline?: string;
    socialNetworks: Array<SocialNetwork>;
    website: Website;
    headshot: string;
    coverphoto: string;
  };

  categories: string[];
  status: ProjectStatusEnum;
}
export interface ProjectResponse {
  categories: string[];
  notes: string;
  status: ProjectStatusEnum;
  updatedAt: string;
  createdAt: string;
  projectRequest: ProjectRequest;
  description: string;
  id: string;
  name: string;
  influencerId: string;
  creatorId: string;
  projects: Project[];
  artStyleId?: string;
}
[];
export interface createProjectPayload {
  influencer_id: string,
  licenseId: string,
  artStyleId: string,
  name: string,
  description: string,
  notes?: string,
  licenseType?: string;
  licenseDuration?: number | string;
  licenseBegins?: string;
  licensePrice?: number;
}

export interface ProjectAssetResponse {
  id?: string;
  projectId: string;
  file: {
    bucket: string;
    key: string;
  };
  createdAt?: string;
}

export interface MyImagesCampaignResponse {
  images: ImageGenerationCampaignRecord[];
}

type ImageApproval = {
  imageId: string;
  status: ImageStatusEnum;
  influencerComment?: string;
  creatorComment?: string;
  createdAt: string;
  updatedAt: string;
  influencerName: string;
  creatorName: string;
  influencerCommentDate: string;
  creatorCommentDate: string;
};

/** Content item from GET /projects/{projectId}/content/list-approvals */
export interface ContentListApprovalItem {
  contentId: string;
  projectId: string;
  mediaUrl: string;
  fileType: string;
  contentMetadata: Record<string, unknown> | null;
  status: string;
  uploaderId: string;
  uploaderName: string;
  requesterComment: string | null;
  requesterCommentDate: string | null;
  approverComment: string | null;
  approverCommentDate: string | null;
  approverName: string | null;
  updatedAt: string;
}
export interface ImageGenerationCampaignRecord {
  status: ImageStatusEnum;
  id: string;
  url: string;
  createdAt: string;
  bookmarked: boolean;
  liked?: boolean;
}

const createProject = async (payload: createProjectPayload) => {
  try {
    const res = await api.post('/subscriptions/v1/projects', payload);
    return res.data;
  } catch (error) {
    console.error('Create project failed:', error);
    throw error;
  }
};

const postProjectAssetsByProjectID = async (project_id: string, payload: string) => {
  try {
    const file = dataURLToFile(payload, 'image.jpg');
    const formData = new FormData();
    formData.append('image', file);
    const res = await projectsClient.post(`/projects/${project_id}/assets`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  } catch (error) {
    console.error('Post project assets failed:', error);
    throw error;
  }
};

const useFetchProjectsByCreatorInfinite = (user_id: string) => {

  const getKey = (pageIndex: number, previousPageData: ProjectResponse | null) => {
    if (previousPageData && previousPageData.projects.length === 0) return null;
    return `/projects/creator/${user_id}?offset=${pageIndex * PAGE_SIZE}&size=${PAGE_SIZE}`;
  };

  const { data, error, size, setSize, mutate } = useSWRInfinite<ProjectResponse>(getKey, fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 60000
  });

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data?.[size - 1] === 'undefined');
  const hasMore = data ? data?.[data?.length - 1]?.projects?.length === PAGE_SIZE : true;

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setSize(size + 1);
    }
  };

  return {
    projects: data?.flatMap(e => e) ?? [],
    isLoadingInitialData,
    isLoadingMore,
    isError: error,
    loadMore,
    hasMore,
    mutate
  };
};

const useFetchProjectsByInfluencerInfinite = (userId: string) => {
  const getKey = (pageIndex: number, previousPageData: ProjectResponse | null) => {
    if (previousPageData && previousPageData.projects.length === 0) return null;
    return `/projects/influencer/${userId}?offset=${pageIndex * PAGE_SIZE}&size=${PAGE_SIZE}`;
  };

  const { data, error, size, setSize, mutate } = useSWRInfinite<ProjectResponse>(getKey, fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 60000
  });

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const hasMore = data ? data?.[data?.length - 1]?.projects?.length === PAGE_SIZE : true;

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setSize(size + 1);
    }
  };

  return {
    projects: data?.flatMap(e => e) ?? [],
    isLoadingInitialData,
    isLoadingMore,
    isError: error,
    loadMore,
    hasMore,
    mutate
  };
};

export const useCreatorProfile = (userId: string | null) => {
  const shouldFetch = Boolean(userId);
  const { data, error, isLoading, mutate } = useSWR<{ data: Creator }>(shouldFetch ? `/users/v1/users/${userId}/creator-profile` : null, api);

  return {
    creatorProfile: data?.data,
    isLoading,
    isError: error,
    mutate
  };
};

export const useInfluencerProfile = (userId: string | null) => {
  const shouldFetch = Boolean(userId);
  const { data, error, isLoading, mutate } = useSWR<{ data: Influencer }>(shouldFetch ? `/users/v1/users/${userId}/influencer-profile` : null, api);

  return {
    influencerProfile: data?.data,
    isLoading,
    isError: error,
    mutate
  };
};

export const useUserProfile = (userId: string | null) => {
  const shouldFetch = Boolean(userId);
  const { data, error, isLoading, mutate } = useSWR<{
    data: { givenName: string; familyName: string };
  }>(shouldFetch ? `/users/v1/users/${userId}` : null, api);

  return {
    userProfile: data?.data,
    isLoading,
    isError: error,
    mutate
  };
};

const updateProjectRequest = async (
  project_id: string,
  payload: {
    influencerComment: string;
    status: ProjectStatusEnum;

  }
) => {
  try {
    const res = await projectsClient.put(`/projects/${project_id}/request`, payload);
    return res.data;
  } catch (error) {
    console.error('Create project request failed:', error);
    throw error;
  }
};

const deleteProject = async (project_id: string) => {
  try {
    const res = await projectsClient.delete(`/projects/${project_id}`);
    return res.data;
  } catch (error) {
    console.error('Delete project failed:', error);
    throw error;
  }
};

const fetchProjectById = async (project_id: string) => {
  try {
    const res = await projectsClient.get(`/projects/${project_id}`);
    return res.data;
  } catch (error) {
    console.error('Delete project failed:', error);
    throw error;
  }
};

const fetchAssetsByProjectID = async (url: string) => {
  const res = await projectsClient.get(url);
  return res.data;
};

// const useFetchAssetsByProjectIDInfinite = (projectId: string) => {
//   const getKey = (pageIndex: number, previousPageData: CampaignAsset[] | null) => {
//     if (!projectId) return null;
//     if (previousPageData && previousPageData.length < PAGE_SIZE) return null; // no more data
//     return `/projects/${projectId}/assets?offset=${pageIndex * PAGE_SIZE}&limit=${PAGE_SIZE}`;
//   };

//   const { data, error, size, setSize, isValidating, mutate } = useSWRInfinite<CampaignAsset[]>(getKey, fetcher);

//   return {
//     data: data?.flat() ?? [],
//     error,
//     isLoadingInitialData: !data && !error,
//     isLoadingMore: isValidating && size > 0,
//     hasMore: data ? data[data.length - 1]?.length === PAGE_SIZE : false,
//     loadMore: () => setSize(size + 1),
//     mutate
//   };
// };

const fetchAssetByID = async (asset_id: string) => {
  try {
    const res = await projectsClient.get(`/projects/assets/${asset_id}`);
    return res.data;
  } catch (error) {
    console.error('Fetch asset by ID failed:', error);
    throw error;
  }
};

const updateProject = async (project_id: string, payload: any) => {
  try {
    const res = await projectsClient.put(`/projects/${project_id}`, payload);
    return res.data;
  } catch (error) {
    console.error('Update project failed:', error);
    throw error;
  }
};

const deleteAllProjects = async () => {
  try {
    const res = await projectsClient.delete('/projects');
    return res.data;
  } catch (error) {
    console.error('Delete all projects failed:', error);
    throw error;
  }
};

const deleteAsset = async (asset_id: string) => {
  try {
    const res = await projectsClient.delete(`/projects/assets/${asset_id}`);
    return res.data;
  } catch (error) {
    console.error('Delete asset failed:', error);
    throw error;
  }
};

const useFetchCampaignIDInfinite = ({ projectId }: { projectId: string }) => {
  const getKey = (pageIndex: number, previousPageData: MyImagesCampaignResponse | null) => {
    if (previousPageData && previousPageData.images.length === 0) return null;
    return `/projects/${projectId}/campaigns?offset=${pageIndex * PAGE_SIZE}&size=${PAGE_SIZE}`;
  };

  const { data, error, size, setSize, mutate } = useSWRInfinite<MyImagesCampaignResponse>(getKey, fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 60000
  });

  const images = data ? data.flatMap(page => page.images) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isError = error;
  const hasMore = data ? data[data.length - 1].images.length === PAGE_SIZE : true;
  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setSize(size + 1);
    }
  };
  return {
    images,
    isLoadingInitialData,
    isLoadingMore,
    isError,
    loadMore,
    hasMore,
    mutate
  };
};

const useFetchImagesByProjectIDInfinite = ({ projectId }: { projectId: string | null | undefined }) => {
  const getKeyFactory =
    (projectId: string | null | undefined) =>
      (pageIndex: number, previousPageData: MyImagesCampaignResponse | null) => {
        if (projectId == null || projectId === '') return null;
        if (previousPageData && previousPageData.images.length < PAGE_SIZE) return null;
        const offset = pageIndex * PAGE_SIZE;
        return `/image-generation/v1/images/project/${projectId}?offset=${offset}&size=${PAGE_SIZE}`;
      };

  const getKey = getKeyFactory(projectId);

  const fetcher = (url: string) => api.get(url).then(res => res.data);

  const { data, error, size, setSize, mutate } =
    useSWRInfinite<MyImagesCampaignResponse>(getKey, fetcher, {
      revalidateOnFocus: true,
      dedupingInterval: 60000,
    });


  const images = data ? data.flatMap(page => page.images) : [];

  const isLoadingInitialData = !data && !error;

  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');

  const isError = !!error;

  const isReachingEnd = data && data[data.length - 1]?.images.length < PAGE_SIZE;

  const hasMore = !isReachingEnd;

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      console.log('Loading more images...');
      setSize(size + 1);
    }
  };
  return {
    images,
    isLoadingInitialData,
    isLoadingMore,
    isError,
    loadMore,
    hasMore,
    mutate
  };
};

const useFetchImageApprovals = ({ projectId }: { projectId?: string }) => {
  const shouldFetch = Boolean(projectId);
  const { data, error, mutate, isLoading } = useSWR<ImageApproval[]>(
    shouldFetch ? `/projects/${projectId}/images/list-approvals` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 60000
    }
  );

  return {
    data,
    error,
    mutate,
    isLoading
  };
};

const useFetchContentListApprovals = ({ projectId }: { projectId?: string }) => {
  const shouldFetch = Boolean(projectId);
  const { data, error, mutate, isLoading } = useSWR<ContentListApprovalItem[]>(
    shouldFetch ? `projects/${projectId}/content/list-approvals` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 60000
    }
  );

  return {
    data: data ?? [],
    error,
    mutate,
    isLoading
  };
};

export {
  useFetchContentListApprovals,
  useFetchProjectsByCreatorInfinite,
  useFetchProjectsByInfluencerInfinite,
  updateProjectRequest,
  postProjectAssetsByProjectID,
  fetchAssetsByProjectID,
  fetchAssetByID,
  createProject,
  useFetchImagesByProjectIDInfinite,
  updateProject,
  deleteAllProjects,
  deleteProject,
  deleteAsset,
  fetchProjectById,
  useFetchCampaignIDInfinite,
  useFetchImageApprovals
};
